/**
 * InfinitSales - Auth Context
 * Authentication state management with onboarding support
 * 
 * UPDATED: 
 * - Removed infinitagentsApiKey from register (now automatic)
 * - Added updateUser function and onboarding-aware redirects
 * FIX: Added token to context value (was missing, breaking SSE connection)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/global/authService';
import onboardingService from '../services/global/onboardingService';
import requestCache from '../services/global/requestCache';
import sseService, { SSE_EVENTS, SSE_EXP_CHECK_ENABLED } from '../services/global/sseService';
import { getJwtExpMs, JWT_EXP_MARGIN_MS } from '../utils/jwt';
import { isDelegated, endDelegation } from '../utils/delegation';
import { getBootFlag, preloadFeatureFlags } from '../hooks/useFeatureFlags';
import { preloadChannels } from '../hooks/useChannels';

const AuthContext = createContext(null);

// Sessão delegada vencida: tempo que o aviso fica na tela antes do reload
// para /empresa (o caminho do api.js — endDelegation + /empresa — é um reload
// completo, que derrubaria o toast na hora).
const SESSION_EXPIRED_DELEGATED_REDIRECT_MS = 2_500;
// setTimeout estoura acima de 2^31-1 ms (~24,8 dias) e dispara na hora.
const MAX_TIMEOUT_MS = 2_147_483_647;

// Frente C8 (flag `optimistic_auth`): com usuario e token no localStorage, o
// app renderiza NA HORA com esse usuario e revalida GET /users/me em segundo
// plano. Antes a tela de carregamento esperava o /users/me inteiro mesmo com
// a sessao ja em cache. Token invalido continua derrubando a sessao: o
// interceptor de 401 em services/global/api.js limpa o storage e manda para
// /login. A flag e lida pela copia persistida (getBootFlag), porque aqui e
// antes de qualquer fetch: ligada no backend, passa a valer do segundo
// carregamento em diante.
function bootOptimistic() {
  return getBootFlag('optimistic_auth');
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Sessao em cache (usuario + token no localStorage) e flag de boot ligada:
  // ja nasce autenticado e sem `loading`. Sem a flag, tudo como hoje.
  const [bootSession] = useState(() => {
    if (!bootOptimistic()) return null;
    try {
      const storedUser = authService.getStoredUser();
      const storedToken = authService.getToken();
      return storedUser && storedToken ? { user: storedUser, token: storedToken } : null;
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(bootSession ? bootSession.user : null);
  const [token, setToken] = useState(bootSession ? bootSession.token : null); // ✅ ADD: Token state
  const [loading, setLoading] = useState(!bootSession);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(bootSession));
  // Flag REACT_APP_SSE_EXP_CHECK: aviso "Sua sessão expirou" (renderizado por
  // SessionExpiredToast no App.js, que sobrevive ao logout). null = sem aviso.
  const [sessionExpired, setSessionExpired] = useState(null);
  const sessionExpiredRef = useRef(false);

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = authService.getStoredUser();
        const storedToken = authService.getToken(); // ✅ Get token

        if (storedUser && storedToken) {
          // Flags cedo: os shells decidem na montagem (inbox_store etc.).
          preloadFeatureFlags();
          // Mapa de capacidades por canal (404 com a flag OFF = copia local).
          preloadChannels();
          // Verify token is still valid by fetching profile
          try {
            const response = await authService.getProfile();
            const freshUser = response.data;

            // Update stored user with fresh data
            authService.storeAuthData(freshUser, storedToken);
            // Boot otimista: so troca a identidade do objeto se o payload
            // mudou, para nao re-executar todo efeito com [user] nas deps.
            setUser((prev) => (
              prev && JSON.stringify(prev) === JSON.stringify(freshUser) ? prev : freshUser
            ));
            setToken(storedToken); // ✅ SET token
            setIsAuthenticated(true);
          } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
              // Token de fato inválido/revogado → encerra a sessão.
              console.warn('Token expired or invalid, logging out');
              authService.logout();
              setUser(null);
              setToken(null); // ✅ CLEAR token
              setIsAuthenticated(false);
            } else {
              // Falha transitória (rede, backend reiniciando): manter a sessão
              // cacheada em vez de deslogar. Deslogar aqui derrubava sessões
              // delegadas e usuários válidos a cada blip de infraestrutura.
              console.warn('Profile refresh failed (transient), keeping cached session');
              setUser((prev) => prev || storedUser);
              setToken(storedToken);
              setIsAuthenticated(true);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Refresh user data when the tab regains focus.
   *
   * Conta Empresa: when the admin changes the shared agent or rotates the
   * api_key, members in another tab/device only see the new values after
   * a manual reload. We piggyback on `visibilitychange` so when the member
   * comes back to the tab, AuthContext re-fetches their profile and the
   * inherited fields update without polling.
   *
   * Throttled to once every 30 seconds to avoid spamming the backend if
   * the user is swapping tabs quickly.
   */
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let lastFetch = Date.now();
    const MIN_INTERVAL = 30_000; // 30s

    const onVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFetch < MIN_INTERVAL) return;
      lastFetch = now;
      try {
        const response = await authService.getProfile();
        const freshUser = response.data;
        const currentToken = authService.getToken();
        if (currentToken && freshUser) {
          authService.storeAuthData(freshUser, currentToken);
          // Só troca a IDENTIDADE do objeto quando o payload realmente mudou.
          // Antes, todo retorno de aba ≥30s fazia setUser incondicional com um
          // objeto novo de mesmos dados — e cada efeito com [user] nas deps
          // (EmpresaDashboard, MemberWelcomeModal, useCreateCompanyNotification)
          // re-executava, refazendo companies/me, limits, subscription/status
          // e remontando o WorkspacesGrid (os GETs 2x/3x vistos em prod).
          setUser((prev) => {
            if (prev && JSON.stringify(prev) === JSON.stringify(freshUser)) {
              return prev;
            }
            return freshUser;
          });
        }
      } catch (err) {
        // Silently fail — visibility refresh is a nice-to-have, not critical.
        // A 401 (member was suspended/removed) is caught by axios interceptors
        // and triggers the global logout flow elsewhere.
        console.debug('[AuthContext] visibility refresh skipped:', err?.message);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated]);

  /**
   * Register new user
   * 
   * InfinitAgents API key is now generated automatically by the backend.
   * If integration fails, use authService.retryInfinitAgentsIntegration()
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} name - User name
   */
  const register = useCallback(async (email, password, name) => {
    // 🆕 UPDATED: No longer passing infinitagentsApiKey (now automatic)
    const response = await authService.register(email, password, name);
    const { user: newUser, token: newToken } = response.data;

    // Conta nova na mesma aba: nada do cache de requests anterior vale.
    requestCache.clearAll();
    authService.storeAuthData(newUser, newToken);
    setUser(newUser);
    setToken(newToken); // ✅ SET token
    setIsAuthenticated(true);
    
    return newUser;
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (email, password) => {
    const response = await authService.login(email, password);
    const { user: loggedInUser, token: newToken } = response.data;

    // Troca de conta na mesma aba: derruba caches da sessão anterior.
    requestCache.clearAll();
    authService.storeAuthData(loggedInUser, newToken);
    setUser(loggedInUser);
    setToken(newToken); // ✅ SET token
    setIsAuthenticated(true);
    
    return loggedInUser;
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    authService.logout();
    // Logout manual navega via SPA (sem reload) — o cache de módulo
    // sobreviveria e o próximo login na MESMA aba leria empresa/plano da
    // conta anterior. Zera aqui.
    requestCache.clearAll();
    setUser(null);
    setToken(null); // ✅ CLEAR token
    setIsAuthenticated(false);
  }, []);

  /**
   * Sessão vencida (JWT com `exp` no passado) — flag REACT_APP_SSE_EXP_CHECK.
   *
   * Mesmo desfecho do 401 do interceptor em services/global/api.js, mas com
   * aviso antes:
   * - sessão delegada: restaura o token real (endDelegation) e volta para
   *   /empresa por reload, depois de deixar o toast visível alguns segundos;
   * - sessão normal: `logout()` (mesmo caminho do botão Sair) — o
   *   ProtectedRoute manda para /login e o SessionExpiredToast também navega
   *   para lá; o toast continua na tela porque é montado fora das rotas.
   * Idempotente por token: SSE e timer podem disparar os dois.
   */
  const expireSession = useCallback((reason = 'token_expired') => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    const delegated = isDelegated();
    console.warn(`[AuthContext] Sessão expirada (${reason}), delegada=${delegated}`);
    setSessionExpired({ delegated, reason, at: Date.now() });
    if (delegated) {
      window.setTimeout(() => {
        endDelegation();
        window.location.href = '/empresa';
      }, SESSION_EXPIRED_DELEGATED_REDIRECT_MS);
      return;
    }
    logout();
  }, [logout]);

  const dismissSessionExpired = useCallback(() => setSessionExpired(null), []);

  /**
   * Flag REACT_APP_SSE_EXP_CHECK: (a) o sseService parou de reconectar
   * porque o token venceu → AUTH_EXPIRED; (b) timer no próprio `exp` do
   * token, para a aba avisar na hora em vez de esperar o SSE cair.
   */
  useEffect(() => {
    if (!SSE_EXP_CHECK_ENABLED || !isAuthenticated || !token) return undefined;
    // Token novo (login/registro na mesma aba): pode expirar de novo.
    sessionExpiredRef.current = false;

    const unsubscribe = sseService.on(SSE_EVENTS.AUTH_EXPIRED, () => expireSession('sse'));

    let timer = null;
    const expMs = getJwtExpMs(token);
    if (expMs != null) {
      const delay = Math.max(0, Math.min(expMs - JWT_EXP_MARGIN_MS - Date.now(), MAX_TIMEOUT_MS));
      timer = window.setTimeout(() => expireSession('timer'), delay);
    }

    return () => {
      unsubscribe();
      if (timer) window.clearTimeout(timer);
    };
  }, [isAuthenticated, token, expireSession]);

  /**
   * Update user in context (without re-fetching)
   * Used after onboarding, channel selection, etc.
   */
  const updateUser = useCallback((updatedUser) => {
    const currentToken = authService.getToken();
    if (currentToken) {
      authService.storeAuthData(updatedUser, currentToken);
    }
    setUser(updatedUser);
  }, []);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getProfile();
      const freshUser = response.data;
      const currentToken = authService.getToken();
      
      if (currentToken) {
        authService.storeAuthData(freshUser, currentToken);
      }
      setUser(freshUser);
      
      return freshUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  }, []);

  /**
   * Get redirect path based on user state
   */
  const getRedirectPath = useCallback(() => {
    return onboardingService.getRedirectPath(user);
  }, [user]);

  /**
   * Check if user needs onboarding
   */
  const needsOnboarding = useCallback(() => {
    return onboardingService.needsOnboarding(user);
  }, [user]);

  // 🏢 Conta Empresa derivatives — expose role/company shortcuts so consumers
  // don't need to read user?.company_role in every component.
  const companyId = user?.company_id || null;
  const companyRole = user?.company_role || null;
  const isCompanyAdmin = companyRole === 'admin';
  const isInCompany = !!companyId;
  // "Membro" para a UI = está numa company e NÃO é o admin. Inclui o papel
  // 'manager' (Gestor, leva 26/08): ele navega como membro (pool de
  // workspaces + abas extras conforme permissão), nunca como admin — sem
  // isto o Gestor perderia o menu Empresa inteiro por não ser 'member'.
  const isCompanyMember = isInCompany && !isCompanyAdmin;
  const accountStatus = user?.account_status || 'active';

  const value = {
    user,
    token, // ✅ ADD: Expose token to consumers (SSEContext needs this!)
    loading,
    isAuthenticated,
    register,
    login,
    logout,
    updateUser,
    refreshUser,
    getRedirectPath,
    needsOnboarding,
    // Sessão expirada (flag REACT_APP_SSE_EXP_CHECK) — consumido por
    // components/notifications/SessionExpiredToast.jsx
    sessionExpired,
    dismissSessionExpired,
    // Conta Empresa
    companyId,
    companyRole,
    isCompanyAdmin,
    isCompanyMember,
    isInCompany,
    accountStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;